import { ButtonLink } from '@/components/ButtonLink'
import { BRANDSIDE_SERVICE_URL } from '@/config'
import { useClickOutside } from '@/hooks/clickOutside'
import { useMarketingUrl } from '@/hooks/MarketingUrl'
import { MktuClassCategory, MktuClassCategoryTitle } from '@/models/Mktu'
import { MktuClass, MktuClassWord } from '@/services/Api/generated'
import { Word } from '@/shared/Mktu/components'
import { mktuClassListStore } from '@/store/MktuClassList.store'
import { onlineSearchStore } from '@/store/OnlineSearch.store'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react'
import cn from 'classnames'
import React, {
  CSSProperties,
  FunctionComponent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styles from './AppSearchMktuButton.module.scss'

type AppSearchMktuButtonProps = {
  mktuClass: MktuClass
  isActive?: boolean
  withDescriptionPopup?: boolean
  onHoverClass?: (selectedClass: MktuClass) => void
  onSelectClass?: (selectedClass: MktuClass) => void
  onLeaveHoverMktuClass?: () => void
}
export const AppSearchMktuButton: FunctionComponent<
  AppSearchMktuButtonProps
> = ({
  mktuClass,
  isActive,
  withDescriptionPopup,
  onHoverClass,
  onSelectClass,
  onLeaveHoverMktuClass,
}) => {
  const classLink = useMemo(
    () => `/mktu/classes/${mktuClass?.classNumber}`,
    [mktuClass]
  )

  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false)
  const mouseEnterTimer = useRef<ReturnType<typeof setTimeout>>(
    setTimeout(() => {})
  )
  const mouseLeaveTimer = useRef<ReturnType<typeof setTimeout>>(
    setTimeout(() => {})
  )

  // Floating UI
  const { refs, floatingStyles } = useFloating({
    open: isDescriptionVisible,
    onOpenChange: setIsDescriptionVisible,
    placement: 'bottom-start',
    whileElementsMounted: isDescriptionVisible ? autoUpdate : undefined,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  })

  // Закрытие по клику вне
  useClickOutside((e: any) => {
    if (
      refs.floating.current &&
      !refs.floating.current.contains(e.target) &&
      refs.reference.current &&
      refs.reference.current instanceof Element &&
      !refs.reference.current.contains(e.target)
    ) {
      setIsDescriptionVisible(false)
    }
  })

  const onMouseLeave = useCallback(() => {
    if (withDescriptionPopup) {
      if (window.innerWidth >= 1024) {
        clearTimeout(mouseLeaveTimer.current)
        clearTimeout(mouseEnterTimer.current)
        mouseLeaveTimer.current = setTimeout(() => {
          setIsDescriptionVisible(false)
        }, 300)
      }
    } else {
      if (typeof onLeaveHoverMktuClass === 'function') {
        onLeaveHoverMktuClass()
      }
    }
  }, [])

  const onMouseEnter = useCallback(() => {
    if (withDescriptionPopup) {
      if (window.innerWidth >= 1024) {
        clearTimeout(mouseLeaveTimer.current)
        clearTimeout(mouseEnterTimer.current)
        mouseEnterTimer.current = setTimeout(() => {
          setIsDescriptionVisible(true)
        }, 300)
      }
    } else {
      if (window.innerWidth >= 1024) {
        if (typeof onHoverClass === 'function' && mktuClass) {
          onHoverClass(mktuClass)
        }
      }
    }
  }, [])

  const clickButton: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.stopPropagation()
      if (withDescriptionPopup) {
        if (window.innerWidth < 1024) {
          event.preventDefault()
          setIsDescriptionVisible(true)
        }
      } else {
        event.preventDefault()
        if (typeof onSelectClass === 'function' && mktuClass) {
          onSelectClass(mktuClass)
        }
      }
    },
    []
  )

  return (
    <div
      className={styles.container}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      ref={withDescriptionPopup ? refs.setReference : null}
    >
      <div
        className={cn(styles.button, {
          [styles.isActive]: isActive,
        })}
        onClick={clickButton}
      >
        {mktuClass.classNumber}
      </div>
      {withDescriptionPopup ? (
        <FloatingPortal>
          <MktuClassDescription
            ref={refs.setFloating}
            mktuClass={mktuClass}
            category={
              mktuClass.classNumber > 34
                ? MktuClassCategory.Product
                : MktuClassCategory.Service
            }
            isVisible={isDescriptionVisible}
            style={floatingStyles}
          />
        </FloatingPortal>
      ) : null}
    </div>
  )
}

type MkutClassDescriptionProps = {
  mktuClass: MktuClass
  category: MktuClassCategory
  isVisible?: boolean
  onlyRead?: boolean
  style: CSSProperties
}
const MktuClassDescription = React.forwardRef<
  HTMLDivElement,
  MkutClassDescriptionProps
>(({ mktuClass, category, isVisible, style }, ref) => {
  const [words, setWords] = useState<Array<MktuClassWord>>([])
  const marketingUrl = useMarketingUrl()
  const toLkButtonLInk = marketingUrl(
    onlineSearchStore.registryInfo?.registrationNumber
      ? `${BRANDSIDE_SERVICE_URL ?? ''}/registries/TM/TM-RUTM${onlineSearchStore.registryInfo.registrationNumber}`
      : '#'
  )

  useEffect(() => {
    mktuClassListStore.getWords(mktuClass.classNumber).then((words) => {
      if (words) {
        setWords(words)
      }
    })
  }, [mktuClass.classNumber])

  return (
    <div
      className={cn(styles.tooltip, {
        [styles.isVisible]: isVisible,
      })}
      style={style}
      ref={ref}
    >
      <div className={styles.tooltipHeader}>
        <div className={styles.tooltipTitle}>Класс {mktuClass.classNumber}</div>
        <div className={styles.tooltipType}>
          {MktuClassCategoryTitle[category]}
        </div>
      </div>
      <div className={styles.tooltipDescription}>
        {mktuClass.classDescription}
      </div>
      {words.length != 0 ? (
        <>
          <div className={styles.selectedWords}>
            {words.slice(0, 1).map((word) => (
              <Word
                maxWidth={412}
                className={styles.word}
                key={word.uuid}
                name={word.word}
                id={word.uuid}
                isNice={word.isNice}
              />
            ))}

            {words.length > 1 ? (
              <div className={styles.moreWords}>+{words.length - 1}</div>
            ) : null}
          </div>
        </>
      ) : null}
      <ButtonLink
        href={toLkButtonLInk}
        target="_blank"
        className={styles.toLkButton}
      >
        Полный перечень в Личном Кабинете
      </ButtonLink>
    </div>
  )
})

MktuClassDescription.displayName = 'MktuClassDescription'
